import { useMemo } from 'react'

import { formatPrice, orderFlowPriceFormatOptions } from '@/_lib/utils'
import { IPriceListItem } from '@/_types'
import { SECTION_THEME_ENUM } from '@/_types/base'
import { PRICE_GROUP_ITEM_FORM_TYPE_ENUM } from '@/_types/price-group'

import { ZOrderProductCard } from '../z-order-product-card'

/**
 * Horizontal row wrapper for the “Cena:” label and value in {@link AProductPrice}.
 */
function AProductPriceWrapper({ children }: { children: React.ReactNode }) {
	return (
		<div className='flex items-center justify-start gap-x-4 whitespace-nowrap text-base text-medante-purple-dark'>
			{children}
		</div>
	)
}

/**
 * Renders price information for a price-list item: `priceText`, numeric price, or nothing.
 *
 * @param props - Price row props.
 * @param props.priceListItem - Item whose `price`, `priceText`, and `priceOld` drive the display.
 * @returns Price row or `null` when there is no usable price data.
 */
function AProductPrice({ priceListItem }: { priceListItem: IPriceListItem }) {
	// Don't show if there's no price information
	if (!priceListItem.price && !priceListItem.priceText) {
		return null
	}

	// If there's priceText and no numeric price, show priceText
	if (
		priceListItem.priceText &&
		priceListItem.priceText?.trim()?.length &&
		!priceListItem.price
	) {
		return (
			<AProductPriceWrapper>
				<span className='inline-block min-w-18 font-medium'>Cena:</span>
				<span className='font-medium'>{priceListItem.priceText}</span>
			</AProductPriceWrapper>
		)
	}

	const formattedPrice = formatPrice(priceListItem.price ?? 0, orderFlowPriceFormatOptions)
	const formattedOldPrice = formatPrice(priceListItem.priceOld, orderFlowPriceFormatOptions)

	// Regular price with or without discount
	return (
		<AProductPriceWrapper>
			<span className='inline-block min-w-18 font-medium'>Cena:</span>

			<div>
				<span className='font-medium'>
					{formattedPrice.price} {formattedPrice.currency}
				</span>

				{priceListItem.priceOld !== null && (
					<span className='ml-2 line-through text-medante-purple-dark/50'>
						{formattedOldPrice.price} {formattedOldPrice.currency}
					</span>
				)}
			</div>
		</AProductPriceWrapper>
	)
}

/**
 * Primary CTA for ordering a price-list item online, or a note when not orderable via web.
 *
 * @param props - Button props.
 * @param props.product - Selected `IPriceListItem` (used for click payload and formatted price).
 * @param props.isOrderable - When false, shows copy that ordering goes through the client centre.
 * @param props.onSelect - Called with `product` when the user clicks and `isOrderable` is true.
 */
function AProductOrderButton({
	product,
	isOrderable,
	onSelect
}: {
	product: IPriceListItem
	isOrderable: boolean
	onSelect: (priceListItem: IPriceListItem) => void
}) {
	if (!isOrderable) {
		return (
			<p className='text-sm italic text-medante-purple-dark/80'>
				Objednateľné cez klientske centrum
			</p>
		)
	}

	const formattedPrice = formatPrice(product.price ?? 0, orderFlowPriceFormatOptions)

	return (
		<button
			className='btn_basic type4 type4-light hover:cursor-pointer whitespace-nowrap w-fit [&>span]:py-0! [&>span]:pl-1.5! py-0.5!'
			onClick={() => onSelect(product)}
		>
			<span className='whitespace-nowrap'>
				Objednajte sa{' '}
				<strong className='font-semibold ml-1'>
					{formattedPrice.price} {formattedPrice.currency}
				</strong>
			</span>

			<span aria-hidden='true'>
				<span>
					<svg className='sico' aria-hidden='true'>
						<use href='/assets/layout/icons.svg#s_ar2_r' />
					</svg>
				</span>
				<span>
					<svg className='sico' aria-hidden='true'>
						<use href='/assets/layout/icons.svg#s_ar2_r' />
					</svg>
				</span>
			</span>
		</button>
	)
}

/**
 * Card for a standalone price-list product: name, optional description, price, and order CTA.
 *
 * @param props - Card props.
 * @param props.product - `IPriceListItem` from the ambulance product list.
 * @param props.onSelect - Invoked when the user orders an online-eligible item.
 *
 * @remarks Online ordering is enabled when `formType` is {@link PRICE_GROUP_ITEM_FORM_TYPE_ENUM.CONTACT_FORM}.
 */
export const AProductCard = ({
	product,
	onSelect,
	sectionTheme = null
}: {
	product: IPriceListItem
	onSelect: (priceListItem: IPriceListItem) => void
	sectionTheme?: SECTION_THEME_ENUM | null
}) => {
	const isOrderable = useMemo(() => {
		return product.formType === PRICE_GROUP_ITEM_FORM_TYPE_ENUM.CONTACT_FORM
	}, [product])

	const description = useMemo(() => {
		return product.description?.trim()?.length ? product.description : null
	}, [product])

	return (
		<ZOrderProductCard split sectionTheme={sectionTheme}>
			<div className='w-full'>
				<p className='mb-1 text-base/5 md:text-lg leading-tight font-semibold lg:font-medium text-medante-purple-dark max-w-xl'>
					{product.name}
				</p>

				{description && (
					<p className='mb-3 text-sm text-medante-purple-dark/90 max-w-lg'>
						{description}
					</p>
				)}
				<AProductPrice priceListItem={product} />
			</div>

			<AProductOrderButton product={product} isOrderable={isOrderable} onSelect={onSelect} />
		</ZOrderProductCard>
	)
}
